import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Chip, TextInput } from 'is24-corecss'
import { AttachmentDataWithRequiredAttachment } from '../../../../../../../../../@types/types'
import _ from 'lodash'
import styles from '../../../../../../../../common/inputs/textInput/TextInput.module.css'
import titleStyles from './TitleSection.module.css'
import { isWebView } from '../../../../../../../../../utils/navigation'

export interface TitleSectionProps {
  attachment: AttachmentDataWithRequiredAttachment
  onTitleChange: (attachment: AttachmentDataWithRequiredAttachment) => void
}

const copyAttachment = (attachment: AttachmentDataWithRequiredAttachment): AttachmentDataWithRequiredAttachment =>
  _.cloneDeep(attachment)

export const shouldSelectChip = (ownTitleUsed: boolean, name: string, currentTitle?: string) =>
  name === currentTitle && !ownTitleUsed

export const loadFrequentNames = (t: Function, key: string) => {
  const result = t(key, { returnObjects: true })
  return Array.isArray(result) ? result : [result]
}

function RemainingCharacters({ remainingCharacters, isMobile }: { remainingCharacters: string; isMobile?: boolean }) {
  const { t } = useTranslation()
  return (
    <span
      className={`float-right font-body-small-regular ${styles.helperTextStyle} ${isMobile ? titleStyles.remainingText : ''}`}
    >
      {t('message.maxLengthLabel', `${remainingCharacters} characters remaining`, {
        value: remainingCharacters,
      })}
    </span>
  )
}

const TitleSection: React.FC<TitleSectionProps> = ({ attachment, onTitleChange }) => {
  const { t } = useTranslation()
  const [ownTitle, setOwnTitle] = useState('')
  const [ownTitleUsed, setOwnTitleUsed] = useState(false)

  const currentAttachmentTitle = attachment.attachment?.title
  const valueLength = ownTitle ? ownTitle.length : 0
  const maxLength = 30

  const frequentNames: string[] = useMemo(() => {
    return loadFrequentNames(t, 'wizard.screen.multimediaNewModalContent.frequentNames')
  }, [t])

  useEffect(() => {
    setOwnTitleUsed(false)
    const title: string | undefined = attachment.attachment?.title
    if (title && !frequentNames.some((name) => name === title)) {
      setOwnTitle(title)
    } else {
      setOwnTitle('')
    }
  }, [attachment, frequentNames])

  const onChipSelected = useCallback(
    (name: string) => {
      const newAttachment: AttachmentDataWithRequiredAttachment = copyAttachment(attachment)
      if (newAttachment.attachment) {
        newAttachment.attachment.title = name
        onTitleChange(newAttachment)
      }
    },
    [attachment, onTitleChange]
  )

  const onOwnTitleChange = useCallback(({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    setOwnTitle(value)
  }, [])

  const onOwnTitleUpdate = useCallback(() => {
    const newAttachment: AttachmentDataWithRequiredAttachment = copyAttachment(attachment)
    if (newAttachment.attachment && ownTitle.trim().length > 0 && ownTitle.trim().length <= maxLength) {
      newAttachment.attachment.title = ownTitle.trim()
      onTitleChange(newAttachment)
      setOwnTitleUsed(true)
    }
  }, [attachment, onTitleChange, ownTitle])

  const onOwnTitleClick = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        onOwnTitleUpdate()
      }
    },
    [onOwnTitleUpdate]
  )

  const remainingChars = maxLength && valueLength ? String(maxLength - valueLength) : String(maxLength)

  const isMobile = isWebView()

  const helperText = <RemainingCharacters remainingCharacters={remainingChars} isMobile={isMobile} />
  return (
    <div className={`grid grid-flex`}>
      <div
        className={`grid-item one-whole font-body-medium-bold ${!isMobile ? 'padding-top-xl padding-bottom-s' : 'padding-bottom-m'}`}
      >
        {isMobile
          ? t('wizard.screen.multimediaNewModalContent.mobile.pleaseSelect', 'Please select')
          : t('wizard.screen.multimediaNewModalContent.pleaseSelect', 'Please select')}
      </div>
      <div className='grid-item one-whole'>
        <div className='grid-flex'>
          {frequentNames.map((name: string) => (
            <span key={name} className='padding-right-m margin-bottom-s'>
              <Chip.Root
                id={name}
                onChange={() => onChipSelected(name)}
                active={shouldSelectChip(ownTitleUsed, name, currentAttachmentTitle)}
              >
                <Chip.Label>{name}</Chip.Label>
              </Chip.Root>
            </span>
          ))}
        </div>
      </div>
      <div className={`grid-item one-whole ${isMobile ? 'order-one-up padding-top-xl padding-bottom-l' : ''}`}>
        <div className='grid-item one-whole padding-top-xs padding-bottom-s font-body-medium-bold'>
          {isMobile
            ? t('wizard.screen.multimediaNewModalContent.mobile.or', 'or')
            : t('wizard.screen.multimediaNewModalContent.or', 'or')}
        </div>
        <div className={`grid-item one-whole`}>
          <TextInput.Root id='ownTitle' data-testid='custom-title-input'>
            <TextInput.Input
              value={ownTitle}
              maxLength={maxLength}
              placeholder={t('wizard.screen.multimediaNewModalContent.ownTitle', 'Own Title')}
              onChange={onOwnTitleChange}
              onBlur={onOwnTitleUpdate}
              onKeyDown={onOwnTitleClick}
            />
            <TextInput.HelperText>{helperText}</TextInput.HelperText>
          </TextInput.Root>
        </div>
      </div>
    </div>
  )
}

export default TitleSection
